/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Stack, Typography, IconButton, CircularProgress, Tooltip,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Check, Sparkles, X } from 'Utils/lucideIcons';
import { ONBOARDING_CHECKLIST_ENABLED } from 'Config/featureFlags';
import {
  ONBOARDING_EVENT,
  getExploredSections,
  getOnboardingDismissed,
  setOnboardingDismissed,
} from 'Utils/onboardingChecklist';
import { SpotlightCard } from './SpotlightCard';
import { buildTheme } from './theme';

/**
 * V2 "Getting Started" banner — the modern-UI counterpart of the classic
 * Components/OnboardingChecklist. Same behaviour, rebuilt on the V2 design
 * language (MUI sx + theme tokens, Lucide icons, SpotlightCard, hover lift)
 * so it sits alongside the app's other cards in both light and dark.
 *
 * Rendered on /access — the projector picker every user lands on after logging
 * in or signing up. That page is near-black, so pass `glass` there: it swaps in
 * the dark theme with a translucent paper, which is all the sx below reads.
 *
 * The close button hides the banner for the rest of the session (per user);
 * logging out clears that, so the next login reminds the user about whatever is
 * still outstanding. The explored sections on the server are the real progress
 * record.
 *
 * It renders nothing once every task is done, so the dashboard returns to its
 * normal layout and established users never see it. The component owns
 * presentation and progress maths; the host page owns the task list. Each task
 * is:
 *
 *   {
 *     key: string,        // an EXPLORED_SECTION key, as stored server-side
 *     label: string,      // full label, e.g. 'Tell Your Story'
 *     hint?: string,      // optional one-liner shown while not started
 *     icon: ReactNode,    // Lucide element, e.g. <Upload size={18} />
 *     onClick: () => void // takes the user straight to the feature
 *   }
 *
 * `explored` is the array /getMyProfile returns; a task is ticked when its key
 * appears in it. Sections marked during this session are merged in on top, so
 * the tick lands immediately rather than after the next profile refresh.
 */

// The /access variant. Everything in this file paints with theme tokens, so a
// dark theme whose `paper` is translucent turns the whole banner into glass
// without a second set of styles. Built once, at module scope.
const GLASS_THEME = (() => {
  const dark = buildTheme('dark');
  return {
    ...dark,
    palette: {
      ...dark.palette,
      background: { ...dark.palette.background, paper: 'rgba(255,255,255,0.06)' },
      divider: 'rgba(255,255,255,0.14)',
    },
  };
})();

// Progress ring: shows the fraction done with the count in the middle, so the
// header carries the whole progress story in one small element.
const ProgressRing = ({ completed, total }) => (
  <Box sx={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
    <CircularProgress
      variant="determinate"
      value={100}
      size={46}
      thickness={3.5}
      sx={{ color: 'divider' }}
    />
    <CircularProgress
      variant="determinate"
      value={total ? (completed / total) * 100 : 0}
      size={46}
      thickness={3.5}
      sx={{
        position: 'absolute',
        left: 0,
        color: 'success.main',
        transition: 'all 400ms ease',
        '& circle': { strokeLinecap: 'round' },
      }}
    />
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, lineHeight: 1 }}>
        {`${completed}/${total}`}
      </Typography>
    </Box>
  </Box>
);

const TaskTile = ({ task, isNext, isLast }) => {
  const { label, hint, icon, done, onClick } = task;
  return (
    // Fills its grid cell, so every tile is the same width and the connector
    // below lands exactly in the grid gap rather than beside a shrunken tile.
    <Box sx={{ position: 'relative', display: 'flex', width: '100%' }}>
      <Box
        role="checkbox"
        aria-checked={done}
        tabIndex={0}
        aria-label={`${label} — ${done ? 'completed' : 'not completed yet, open to complete'}`}
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick?.();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          width: '100%',
          p: 1.25,
          borderRadius: 2,
          border: '1px solid',
          borderColor: done
            ? (theme) => (theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.32)' : 'rgba(22,163,74,0.28)')
            : 'divider',
          bgcolor: done
            ? (theme) => (theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.09)' : 'rgba(22,163,74,0.06)')
            : 'background.paper',
          boxShadow: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'transform 180ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 160ms ease, background-color 160ms ease, box-shadow 180ms ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: done ? 'success.main' : 'text.primary',
            boxShadow: (theme) => (theme.palette.mode === 'dark'
              ? '0 10px 22px rgba(0,0,0,0.35)'
              : '0 10px 22px rgba(15,17,28,0.10)'),
          },
          '&:hover .ocl-ic': { transform: 'scale(1.12) rotate(-6deg)' },
          '&:active': { transform: 'translateY(0)' },
          '&:focus-visible': {
            outline: 'none',
            boxShadow: (theme) => `0 0 0 3px ${theme.palette.mode === 'dark' ? 'rgba(245,245,247,0.28)' : 'rgba(15,23,42,0.16)'}`,
          },
        }}
      >
        <Box
          className="ocl-ic"
          sx={{
            width: 36,
            height: 36,
            flexShrink: 0,
            borderRadius: 1.75,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: done
              ? (theme) => (theme.palette.mode === 'dark' ? 'rgba(34,197,94,0.18)' : 'rgba(22,163,74,0.12)')
              : 'action.hover',
            color: done ? 'success.main' : 'text.secondary',
            transition: 'transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1), color 180ms ease, background-color 180ms ease',
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.25 }} noWrap>
            {label}
          </Typography>
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: 'block',
              lineHeight: 1.35,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.2,
              color: done ? 'success.main' : 'text.secondary',
            }}
          >
            {done ? 'Completed' : (isNext ? 'Start here' : (hint || 'Not started'))}
          </Typography>
        </Box>

        {/* The tick box — empty and dashed until the task is done. Every
            outstanding task looks the same; only "done" changes the styling. */}
        <Box
          aria-hidden="true"
          sx={{
            width: 20,
            height: 20,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: done ? '2px solid' : '2px dashed',
            borderColor: done
              ? 'success.main'
              : (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.22)' : 'rgba(15,17,28,0.18)'),
            bgcolor: done ? 'success.main' : 'transparent',
            color: done ? '#fff' : 'transparent',
            transition: 'border-color 180ms ease, background-color 180ms ease, color 180ms ease',
          }}
        >
          <Check size={13} strokeWidth={3} />
        </Box>
      </Box>

      {/* Dotted link drawn in the grid gap, so the tiles read as one connected
          flow rather than five unrelated buttons. Only from the breakpoint where
          all five sit on a single row — otherwise it would dangle off a row end. */}
      {!isLast && (
        <Box
          aria-hidden="true"
          sx={{
            display: { xs: 'none', lg: 'block' },
            position: 'absolute',
            top: '50%',
            right: -14,
            width: 14,
            height: 2,
            transform: 'translateY(-50%)',
            backgroundImage: (theme) => `linear-gradient(to right, ${done ? theme.palette.success.main : theme.palette.divider} 55%, transparent 55%)`,
            backgroundSize: '5px 2px',
            opacity: done ? 0.8 : 1,
            transition: 'background-image 300ms ease',
          }}
        />
      )}
    </Box>
  );
};

export const OnboardingChecklist = ({
  tasks = [], explored, title = 'Getting Started', glass = false, sx,
}) => {
  const [exploredSections, setExploredSections] = useState(() => getExploredSections(explored));
  const [dismissed, setDismissed] = useState(() => getOnboardingDismissed());

  // A section can be explored from anywhere in the app (and in another tab), so
  // re-read whenever one is marked or the profile brings a fresher list.
  useEffect(() => {
    const sync = () => { setExploredSections(getExploredSections(explored)); };
    sync();
    window.addEventListener(ONBOARDING_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(ONBOARDING_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, [explored]);

  const resolvedTasks = useMemo(() => tasks.map((task) => ({
    ...task,
    done: exploredSections.includes(task.key),
  })), [tasks, exploredSections]);

  const completedCount = resolvedTasks.filter(({ done }) => done).length;
  const totalCount = resolvedTasks.length;
  const allDone = totalCount > 0 && completedCount === totalCount;
  const nextTaskKey = resolvedTasks.find(({ done }) => !done)?.key;

  const dismiss = () => {
    setOnboardingDismissed(true);
    setDismissed(true);
  };

  // Feature switched off in Config/featureFlags, closed by the user, nothing
  // left to guide them through, or no tasks at all — the banner retires itself,
  // which also means users who were already set up never see it.
  if (!ONBOARDING_CHECKLIST_ENABLED || dismissed || totalCount === 0 || allDone) return null;

  const card = (
    <SpotlightCard
      component="section"
      aria-label={title}
      spotlightColor={glass ? 'rgba(90, 165, 239, 0.24)' : 'rgba(90, 165, 239, 0.16)'}
      sx={{
        p: { xs: 1.75, md: 2.5 },
        ...(glass ? {
          // Frosted over the page's aurora, like the rest of /access.
          backdropFilter: 'blur(18px)',
          WebkitBackdropFilter: 'blur(18px)',
          boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
        } : null),
        ...sx,
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: 'center' }}
        >
          {/* Badge — marks the card as onboarding, not another data panel. */}
          <Box
            sx={{
              width: 38,
              height: 38,
              flexShrink: 0,
              borderRadius: 2,
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              justifyContent: 'center',
              color: '#fff',
              background: 'linear-gradient(140deg, #5aa5ef 0%, #2f6fd0 100%)',
              boxShadow: '0 6px 16px rgba(47,111,208,0.28)',
            }}
          >
            <Sparkles size={19} />
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 15, md: 16.5 }, letterSpacing: '-0.01em', lineHeight: 1.25 }}>
              {title}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>
              {`Tick these off to get the most out of your Projector · ${completedCount} of ${totalCount} done`}
            </Typography>
          </Box>

          <ProgressRing completed={completedCount} total={totalCount} />

          {/* Close — hides the banner until the next login. Sized explicitly and
              never allowed to shrink, so the hover/focus ring is a true circle
              rather than an ellipse squeezed by the flex row. */}
          <Tooltip title="Dismiss">
            <IconButton
              size="small"
              onClick={dismiss}
              aria-label="Dismiss getting started checklist"
              sx={{
                width: 32,
                height: 32,
                flex: '0 0 auto',
                borderRadius: '50%',
                color: 'text.secondary',
                '&:hover': { color: 'text.primary' },
              }}
            >
              <X size={18} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box
          component="ul"
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
            gap: { xs: 1, md: 1.75 },
            listStyle: 'none',
            m: 0,
            mt: { xs: 1.5, md: 2 },
            p: 0,
          }}
        >
          {resolvedTasks.map((task, index) => (
            <Box component="li" key={task.key} sx={{ display: 'flex' }}>
              <TaskTile
                task={task}
                isNext={!task.done && task.key === nextTaskKey}
                isLast={index === resolvedTasks.length - 1}
              />
            </Box>
          ))}
        </Box>
      </Box>
    </SpotlightCard>
  );

  return glass ? <ThemeProvider theme={GLASS_THEME}>{card}</ThemeProvider> : card;
};

export default OnboardingChecklist;
